import React, { useState, useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  FiBell, FiLogOut, FiMenu, FiCheckCircle, FiFileText, FiGlobe,
  FiChevronDown, FiUser, FiSettings, FiUserCheck, FiCreditCard,
  FiShield, FiArrowRight, FiBriefcase,
} from 'react-icons/fi';
import { useTranslation } from 'react-i18next';
import { logout } from '../../store/slices/authSlice';
import { fetchNotifications } from '../../store/slices/notificationsSlice';
import { setUserLanguage } from '../../store/slices/languageSlice';
import { notificationsAPI, delegationsAPI, isDelegated } from '../../services/api';
import { formatMonthDayTime } from '../../utils/dateFormat';
import { notificationTarget, notificationText } from '../../utils/notificationDisplay';
import ImpersonateUserModal from './ImpersonateUserModal';
import ReceivedDelegationsModal from './ReceivedDelegationsModal';
import { getLocalized } from '../../i18n/getLocalized';
import './Header.scss';

function Header({ onMobileMenuToggle }) {
  const { t } = useTranslation();
  const { user } = useSelector((state) => state.auth);
  const { items: notifications, unreadCount } = useSelector((state) => state.notifications);
  const { currentLanguage } = useSelector((state) => state.language);
  const { branding, brandingLoading } = useSelector((state) => state.settings);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const [showNotifications, setShowNotifications] = useState(false);
  const [showLangMenu, setShowLangMenu] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showImpersonateModal, setShowImpersonateModal] = useState(false);
  const [showDelegationsModal, setShowDelegationsModal] = useState(false);
  const [receivedDelegations, setReceivedDelegations] = useState([]);
  const dropdownRef = useRef(null);
  const langRef = useRef(null);
  const userMenuRef = useRef(null);

  const PAGE_TITLES = {
    '/dashboard': t('nav.dashboard'),
    '/contracts': t('nav.contracts'),
    '/contracts/new': t('contracts.newContract'),
    '/templates': t('nav.templates'),
    '/approvals': t('nav.approvals'),
    '/audit': t('nav.auditLog'),
    '/settings': t('nav.settings'),
    '/profile': t('settings.accountTitle'),
  };

  const pageTitle = PAGE_TITLES[location.pathname] || '';
  const isTenantOwner = user?.role === 'tenant_owner';
  const isImpersonated = !!user?.impersonation;
  const inDelegatedSession = isDelegated();
  // Whether another stand-in session could be opened from here. The backend
  // refuses nesting either way, so "Log in as" is hidden in both.
  const inScopedSession = isImpersonated || inDelegatedSession;
  // "Bana Verilen Vekaletler" stays fully usable under impersonation —
  // impersonation exists to BE the user, delegations included; the audit
  // trail keeps the impersonator on every record. Only a delegation session
  // hides it: hopping delegate-to-delegate is never valid.
  const canSeeReceivedDelegations = !inDelegatedSession;

  useEffect(() => {
    dispatch(fetchNotifications({ limit: 10 }));
    const interval = setInterval(() => {
      dispatch(fetchNotifications({ limit: 10 }));
    }, 60000);
    return () => clearInterval(interval);
  }, [dispatch]);

  // Delegations granted to me (or, under impersonation, to the user being
  // viewed — the API is bound to the session identity, so it answers for
  // whoever this session is). Fetched once per mount; the modal re-fetches on
  // open, so this only decides whether the entry appears and its count.
  useEffect(() => {
    if (!canSeeReceivedDelegations) return;
    let cancelled = false;
    delegationsAPI.received()
      .then((res) => { if (!cancelled) setReceivedDelegations(res.data?.delegations || []); })
      .catch(() => { /* the menu simply omits the entry */ });
    return () => { cancelled = true; };
  }, [canSeeReceivedDelegations]);

  useEffect(() => {
    function handleClickOutside(e) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setShowNotifications(false);
      }
      if (langRef.current && !langRef.current.contains(e.target)) {
        setShowLangMenu(false);
      }
      if (userMenuRef.current && !userMenuRef.current.contains(e.target)) {
        setShowUserMenu(false);
      }
    }
    function handleKey(e) {
      if (e.key === 'Escape') {
        setShowNotifications(false);
        setShowLangMenu(false);
        setShowUserMenu(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKey);
    };
  }, []);

  const handleLogout = () => {
    setShowUserMenu(false);
    dispatch(logout());
    navigate('/login');
  };

  const handleMarkRead = async (id) => {
    try {
      await notificationsAPI.markRead(id);
      dispatch(fetchNotifications({ limit: 10 }));
    } catch (err) { /* ignore */ }
  };

  // Clicking a notification marks it read and, when the type has a destination
  // (a deactivated user's stranded work → İş Devri, a contract → its detail),
  // navigates there.
  const handleNotificationClick = (n) => {
    if (!n.isRead) handleMarkRead(n._id);
    const target = notificationTarget(n);
    if (target) {
      setShowNotifications(false);
      navigate(target);
    }
  };

  const handleMarkAllRead = async () => {
    try {
      await notificationsAPI.markAllRead();
      dispatch(fetchNotifications({ limit: 10 }));
    } catch (err) { /* ignore */ }
  };

  const handleLanguageSwitch = (lang) => {
    dispatch(setUserLanguage(lang));
    setShowLangMenu(false);
  };

  const formatDate = (d) => formatMonthDayTime(d, '');

  const goTo = (path) => {
    setShowUserMenu(false);
    navigate(path);
  };

  const roleLabel = (role) => {
    if (!role) return '';
    return role.split('_').map(w => w[0].toUpperCase() + w.slice(1)).join(' ');
  };

  const titleDisplay = (u) => {
    if (!u) return '';
    if (u.role === 'tenant_owner') return t('users.roles.tenant_owner', { defaultValue: roleLabel(u.role) });
    return u.titleRef?.name || u.title || '';
  };

  const companyName = getLocalized(branding?.companyName, currentLanguage) || '';
  const logoUrl = branding?.logoUrl || '';

  return (
    <header className="header">
      <div className="header__left">
        <button className="header__mobile-menu mobile-only" onClick={onMobileMenuToggle} aria-label={t('common.openMenu')}>
          <FiMenu />
        </button>

        <div className="header__brand">
          {brandingLoading ? (
            <>
              <div className="skeleton skeleton--logo" />
              <div className="header__brand-text">
                <div className="skeleton skeleton--brand-name" />
                <div className="skeleton skeleton--brand-label" />
              </div>
            </>
          ) : (
            <>
              {logoUrl ? (
                <img
                  src={logoUrl}
                  alt={companyName}
                  className="header__brand-logo-img"
                />
              ) : (
                <div className="header__brand-logo-icon">
                  <FiFileText />
                </div>
              )}
              <div className="header__brand-text">
                <span className="header__brand-name">{companyName}</span>
                <span className="header__brand-label">{t('common.contractManagement')}</span>
              </div>
            </>
          )}
        </div>

        {pageTitle && <div className="header__brand-divider desktop-only" />}
        {pageTitle && <h1 className="header__title desktop-only">{pageTitle}</h1>}

        {/* Mount point for a page's primary action once it scrolls out of
            reach — see StickyHeaderAction. It lives on the left so it can
            never collide with the language / notification / profile cluster,
            and stays empty (zero width) on pages that don't use it. */}
        <div id="header-action-slot" className="header__action-slot" />
      </div>

      <div className="header__right">
        {/* Language Switcher */}
        <div className="header__lang-wrapper" ref={langRef}>
          <button
            className="header__icon-btn header__lang-btn"
            onClick={() => setShowLangMenu(!showLangMenu)}
            aria-haspopup="menu"
            aria-expanded={showLangMenu}
            title={t('common.language')}
          >
            <FiGlobe />
            <span className="header__lang-code desktop-only">{currentLanguage?.toUpperCase()}</span>
          </button>
          {showLangMenu && (
            <div className="header__lang-dropdown" role="menu">
              <button
                role="menuitem"
                className={`header__lang-option ${currentLanguage === 'en' ? 'header__lang-option--active' : ''}`}
                onClick={() => handleLanguageSwitch('en')}
              >
                <span className="header__lang-option-code">EN</span> {t('common.english')}
              </button>
              <button
                role="menuitem"
                className={`header__lang-option ${currentLanguage === 'tr' ? 'header__lang-option--active' : ''}`}
                onClick={() => handleLanguageSwitch('tr')}
              >
                <span className="header__lang-option-code">TR</span> {t('common.turkish')}
              </button>
            </div>
          )}
        </div>

        {/* Notifications */}
        <div className="header__notification-wrapper" ref={dropdownRef}>
          <button
            className="header__icon-btn"
            onClick={() => setShowNotifications(!showNotifications)}
            aria-haspopup="menu"
            aria-expanded={showNotifications}
            aria-label={t('common.notifications')}
          >
            <FiBell />
            {unreadCount > 0 && (
              <span className="header__notification-dot" aria-label={`${unreadCount} unread`}>
                {unreadCount > 9 ? '9+' : unreadCount}
              </span>
            )}
          </button>

          {showNotifications && (
            <div className="header__dropdown" role="menu">
              <div className="header__dropdown-header">
                <span className="header__dropdown-title">{t('common.notifications')}</span>
                {unreadCount > 0 && (
                  <button className="header__dropdown-action" onClick={handleMarkAllRead}>
                    <FiCheckCircle /> {t('common.markAllRead')}
                  </button>
                )}
              </div>
              <div className="header__dropdown-list">
                {(!notifications || notifications.length === 0) ? (
                  <div className="header__dropdown-empty">{t('common.noNotifications')}</div>
                ) : (
                  notifications.map((n) => {
                    const view = notificationText(n, t, currentLanguage);
                    const clickable = !!notificationTarget(n);
                    return (
                      <div
                        key={n._id}
                        className={[
                          'header__dropdown-item',
                          !n.isRead ? 'header__dropdown-item--unread' : '',
                          clickable ? 'header__dropdown-item--link' : '',
                        ].filter(Boolean).join(' ')}
                        onClick={() => handleNotificationClick(n)}
                      >
                        <div className="header__dropdown-item-title">{view.title}</div>
                        <div className="header__dropdown-item-message">{view.message}</div>
                        {view.detail?.length > 0 && (
                          <div className="header__dropdown-item-tags">
                            {view.detail.slice(0, 3).map((label, i) => (
                              <span key={`${n._id}-tag-${i}`}>{label}</span>
                            ))}
                            {view.detail.length > 3 && <span>+{view.detail.length - 3}</span>}
                          </div>
                        )}
                        <div className="header__dropdown-item-time">
                          {formatDate(n.createdAt)}
                          {clickable && (
                            <span className="header__dropdown-item-cta">
                              {t('common.open')} <FiArrowRight />
                            </span>
                          )}
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          )}
        </div>

        <div className="header__divider" />

        {/* User menu */}
        <div className="header__user-wrapper" ref={userMenuRef}>
          {!user ? (
            <div className="skeleton skeleton--avatar" />
          ) : (
            <button
              className={`header__user-trigger ${showUserMenu ? 'header__user-trigger--open' : ''}`}
              onClick={() => setShowUserMenu(!showUserMenu)}
              aria-haspopup="menu"
              aria-expanded={showUserMenu}
            >
              <div className={`header__user-avatar ${isImpersonated || user?.delegation ? 'header__user-avatar--impersonated' : ''}`}>
                {user?.firstName?.[0]}{user?.lastName?.[0]}
              </div>
              <div className="header__user-info desktop-only">
                <span className="header__user-name">
                  {user?.firstName} {user?.lastName}
                </span>
                <span className="header__user-role">{titleDisplay(user)}</span>
              </div>
              <FiChevronDown className="header__user-caret desktop-only" />
            </button>
          )}

          {showUserMenu && user && (
            <div className="header__user-menu" role="menu">
              <div className="header__user-menu-head">
                <div className="header__user-menu-avatar">
                  {user?.firstName?.[0]}{user?.lastName?.[0]}
                </div>
                <div className="header__user-menu-identity">
                  <div className="header__user-menu-name">
                    {user?.firstName} {user?.lastName}
                  </div>
                  <div className="header__user-menu-email">{user?.email}</div>
                  <div className="header__user-menu-role-tag">
                    <FiShield /> {titleDisplay(user)}
                  </div>
                </div>
              </div>

              {user?.delegation && (
                <div className="header__user-menu-impersonation">
                  <FiBriefcase />
                  <div>
                    <div className="header__user-menu-impersonation-title">
                      {t('delegation.activeTitle')}
                    </div>
                    <div className="header__user-menu-impersonation-body">
                      {t('delegation.activeDesc', {
                        delegator: `${user.firstName} ${user.lastName}`,
                        delegate: user.delegation.delegateName,
                      })}
                    </div>
                  </div>
                </div>
              )}

              {isImpersonated && (
                <div className="header__user-menu-impersonation">
                  <FiUserCheck />
                  <div>
                    <div className="header__user-menu-impersonation-title">
                      {t('impersonation.activeTitle')}
                    </div>
                    <div className="header__user-menu-impersonation-body">
                      {t('impersonation.activeDesc', { name: user.impersonation.impersonatorName })}
                    </div>
                  </div>
                </div>
              )}

              <div className="header__user-menu-list">
                <button role="menuitem" className="header__user-menu-item" onClick={() => goTo('/profile')}>
                  <FiUser /> {t('userMenu.profile')}
                </button>
                <button role="menuitem" className="header__user-menu-item" onClick={() => goTo('/profile?tab=language')}>
                  <FiSettings /> {t('userMenu.preferences')}
                </button>
                <button role="menuitem" className="header__user-menu-item" onClick={() => goTo('/profile?tab=delegation')}>
                  <FiBriefcase /> {t('userMenu.myDelegations')}
                </button>
                {isTenantOwner && !isImpersonated && (
                  <button role="menuitem" className="header__user-menu-item" onClick={() => goTo('/subscription')}>
                    <FiCreditCard /> {t('userMenu.subscription')}
                  </button>
                )}
              </div>

              {/* "Bana Verilen Vekaletler" sits above "Log in as" — both open
                  someone else's workspace in a new tab, and a delegation is the
                  one most users will ever have. Hidden when nobody has granted
                  me one, so the menu stays quiet for everyone else. */}
              {canSeeReceivedDelegations && receivedDelegations.length > 0 && (
                <>
                  <div className="header__user-menu-divider" />
                  <div className="header__user-menu-list">
                    <button
                      role="menuitem"
                      className="header__user-menu-item header__user-menu-item--accent"
                      onClick={() => { setShowUserMenu(false); setShowDelegationsModal(true); }}
                    >
                      <FiBriefcase />
                      <div className="header__user-menu-item-stack">
                        <span>
                          {t('userMenu.receivedDelegations')}
                          {' '}
                          <span className="header__user-menu-item-count">
                            {receivedDelegations.length}
                          </span>
                        </span>
                        <span className="header__user-menu-item-hint">
                          {t('userMenu.receivedDelegationsHint')}
                        </span>
                      </div>
                    </button>
                  </div>
                </>
              )}

              {isTenantOwner && !inScopedSession && (
                <>
                  <div className="header__user-menu-divider" />
                  <div className="header__user-menu-list">
                    <button
                      role="menuitem"
                      className="header__user-menu-item header__user-menu-item--accent"
                      onClick={() => { setShowUserMenu(false); setShowImpersonateModal(true); }}
                    >
                      <FiUserCheck />
                      <div className="header__user-menu-item-stack">
                        <span>{t('userMenu.loginAsUser')}</span>
                        <span className="header__user-menu-item-hint">
                          {t('userMenu.loginAsUserHint')}
                        </span>
                      </div>
                    </button>
                  </div>
                </>
              )}

              <div className="header__user-menu-divider" />
              <div className="header__user-menu-list">
                <button
                  role="menuitem"
                  className="header__user-menu-item header__user-menu-item--danger"
                  onClick={handleLogout}
                >
                  <FiLogOut />
                  {isImpersonated
                    ? t('impersonation.exit')
                    : user?.delegation
                      ? t('delegation.exit')
                      : t('common.signOut')}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {showImpersonateModal && (
        <ImpersonateUserModal onClose={() => setShowImpersonateModal(false)} />
      )}

      {showDelegationsModal && (
        <ReceivedDelegationsModal
          delegations={receivedDelegations}
          onClose={() => setShowDelegationsModal(false)}
        />
      )}
    </header>
  );
}

export default Header;
